import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { Icon } from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const LABELS = {
  type: {
    positive: "Positive",
    dangerous: "Dangereuse",
    near_miss: "Presqu'accident",
    "near miss": "Presqu'accident",
    non_conformity: "Non-conformité",
    "non-conformity": "Non-conformité",
  },
  severity: { low: "Faible", medium: "Moyenne", high: "Élevée" },
  status: {
    submitted: "Soumise",
    received: "Reçue",
    in_analysis: "En analyse",
    corrective_action_requested: "Action requise",
    action_completed: "Action réalisée",
    verified: "Vérifiée",
    closed: "Clôturée",
  },
  exposure_duration: {
    momentary: "Momentanée",
    few_minutes: "Quelques minutes",
    few_hours: "Quelques heures",
    full_day: "Journée complète",
    several_days: "Plusieurs jours",
    ongoing: "En cours",
  },
  weather_conditions: {
    sunny: "Ensoleillé",
    cloudy: "Nuageux",
    rainy: "Pluvieux",
    stormy: "Orageux",
    windy: "Venteux",
    foggy: "Brumeux",
    dry: "Sec",
    humid: "Humide",
    other: "Autre",
  },
};

const getTypeColor = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "positive") return "success";
  if (t === "dangerous") return "danger";
  if (t === "near_miss" || t === "near miss") return "warning";
  return "info";
};

const getSeverityColor = (s) => {
  const v = String(s || "").toLowerCase();
  if (v === "low") return "success";
  if (v === "medium") return "warning";
  if (v === "high") return "danger";
  return "secondary";
};

const getStatusColor = (s) => {
  const v = String(s || "").toLowerCase();
  const map = {
    submitted: "secondary",
    received: "info",
    in_analysis: "warning",
    corrective_action_requested: "danger",
    action_completed: "success",
    verified: "primary",
    closed: "dark",
  };
  return map[v] || "secondary";
};

const ObservationViewModal = ({ isOpen, onClose, observationId, onEditClick }) => {
  const [loading, setLoading] = useState(false);
  const [observation, setObservation] = useState(null);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    if (!isOpen || !observationId) {
      setObservation(null);
      return;
    }
    setActiveTab("info");
    loadObservation();
  }, [isOpen, observationId]);

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return "—";
    const n = Number(bytes);
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const loadObservation = async () => {
    if (!observationId) return;
    try {
      setLoading(true);
      const response = await api.getObservation(observationId);
      setObservation(response.data || response);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'observation");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const openAttachment = async (att) => {
    try {
      const blob = await api.openObservationAttachment(att.id);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      toast.error("Impossible d'ouvrir ce fichier.");
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const blob = await api.downloadObservationAttachment(att.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = att.file_name || "piece-jointe";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast.error("Impossible de télécharger ce fichier.");
    }
  };

  const handleEdit = () => {
    onClose();
    onEditClick && onEditClick(observationId);
  };

  const obs = observation;
  const typeLabel = obs && (LABELS.type[obs.type] ?? obs.type);
  const severityLabel = obs && (LABELS.severity[obs.severity] ?? obs.severity);
  const statusLabel = obs && (LABELS.status[obs.status] ?? obs.status);

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="modal-dialog-centered modal-xl">
      {loading ? (
        <ModalBody className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-soft mb-0">Chargement...</p>
        </ModalBody>
      ) : !obs ? null : (
        <>
          <ModalHeader toggle={onClose}>
            <div>
              <h5 className="modal-title mb-1">{obs.title || "Sans titre"}</h5>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {obs.type && (
                  <Badge color={getTypeColor(obs.type)}>{typeLabel}</Badge>
                )}
                {obs.severity && (
                  <Badge color={getSeverityColor(obs.severity)}>{severityLabel}</Badge>
                )}
                {obs.status && (
                  <Badge color={getStatusColor(obs.status)}>{statusLabel}</Badge>
                )}
              </div>
            </div>
          </ModalHeader>

          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <Nav tabs className="nav-tabs-s2 mb-4">
              <NavItem>
                <NavLink
                  href="#info"
                  className={activeTab === "info" ? "active" : ""}
                  onClick={(ev) => {
                    ev.preventDefault();
                    setActiveTab("info");
                  }}
                >
                  <Icon name="info" className="me-1" />
                  Informations
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#proofs"
                  className={activeTab === "proofs" ? "active" : ""}
                  onClick={(ev) => {
                    ev.preventDefault();
                    setActiveTab("proofs");
                  }}
                >
                  <Icon name="clip" className="me-1" />
                  Preuves (fichiers / vidéos)
                </NavLink>
              </NavItem>
            </Nav>

            {activeTab === "info" ? (
              <>
            {/* Infos principales en ligne simple */}
            <div className="row g-3 mb-4 pb-3 border-bottom">
              <div className="col-sm-4">
                <span className="d-block text-soft small mb-1">Site</span>
                <span className="fw-medium">{obs.site?.name || "—"}</span>
              </div>
              <div className="col-sm-4">
                <span className="d-block text-soft small mb-1">Date d'observation</span>
                <span className="fw-medium">
                  {obs.date_observation
                    ? new Date(obs.date_observation).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </span>
              </div>
              <div className="col-sm-4">
                <span className="d-block text-soft small mb-1">Assigné à</span>
                <span className="fw-medium">
                  {obs.assignee
                    ? `${obs.assignee.first_name || ""} ${obs.assignee.last_name || ""}`.trim() || obs.assignee.email
                    : "—"}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <h6 className="text-soft mb-2">
                <Icon name="file-text" className="me-2" />
                Description
              </h6>
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {obs.description || "Aucune description"}
              </p>
            </div>

            {(obs.source_inspection_id || obs.source_inspection?.id) && (
              <div className="alert alert-pro alert-warning mb-4">
                <h6 className="title mb-2">
                  <Icon name="notes-alt" className="me-2" />
                  Inspection source
                </h6>
                <p className="mb-0 small">
                  <a
                    className="link"
                    href={`${process.env.PUBLIC_URL || ""}/hse-process/inspections/${obs.source_inspection_id || obs.source_inspection?.id}`}
                  >
                    Inspection #{obs.source_inspection_id || obs.source_inspection?.id}
                    {obs.source_inspection?.title ? ` — ${obs.source_inspection.title}` : ""}
                  </a>
                </p>
              </div>
            )}

            {((obs.derived_incidents && obs.derived_incidents.length > 0) ||
              (obs.derivedIncidents && obs.derivedIncidents.length > 0)) && (
              <div className="alert alert-pro alert-danger mb-4">
                <h6 className="title mb-2">
                  <Icon name="alert-circle" className="me-2" />
                  Incidents HSE liés
                </h6>
                <ul className="list list-sm mb-0">
                  {(obs.derived_incidents || obs.derivedIncidents || []).map((inc) => (
                    <li key={inc.id}>
                      <a
                        className="link"
                        href={`${process.env.PUBLIC_URL || ""}/hse-process/incidents?open_incident=${inc.id}`}
                      >
                        #{inc.id} — {inc.title}{" "}
                        <span className="text-soft">({inc.status})</span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-soft small mb-0 mt-2">
                  Le lien ouvre la fiche incident (RCA, CAPA) dans Processus HSE.
                </p>
              </div>
            )}

            {obs.risk_potential && (
              <div className="mb-4">
                <h6 className="text-soft mb-2">
                  <Icon name="alert-triangle" className="me-2" />
                  Potentiel de risque
                </h6>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{obs.risk_potential}</p>
              </div>
            )}

            {(obs.equipment_concerned || obs.people_exposed || obs.number_of_people_exposed != null || obs.exposure_duration || obs.immediate_action_taken) && (
              <div className="mb-4">
                <h6 className="text-soft mb-2">
                  <Icon name="users" className="me-2" />
                  Exposition
                </h6>
                <div className="row g-2">
                  {obs.equipment_concerned && (
                    <div className="col-md-4"><span className="text-soft">Équipement :</span> {obs.equipment_concerned}</div>
                  )}
                  {obs.people_exposed && (
                    <div className="col-md-4"><span className="text-soft">Personnes exposées :</span> {obs.people_exposed}</div>
                  )}
                  {obs.number_of_people_exposed != null && (
                    <div className="col-md-4"><span className="text-soft">Nombre :</span> {obs.number_of_people_exposed}</div>
                  )}
                  {obs.exposure_duration && (
                    <div className="col-md-4"><span className="text-soft">Durée :</span> {LABELS.exposure_duration[obs.exposure_duration] ?? obs.exposure_duration}</div>
                  )}
                  {obs.immediate_action_taken && (
                    <div className="col-12"><Badge color="success">Action immédiate prise</Badge></div>
                  )}
                </div>
              </div>
            )}

            {(obs.location_description || obs.location_latitude || obs.location_longitude || obs.weather_conditions) && (
              <div className="mb-4">
                <h6 className="text-soft mb-2">
                  <Icon name="map" className="me-2" />
                  Localisation
                </h6>
                <div className="row g-2">
                  {obs.location_description && (
                    <div className="col-md-4"><span className="text-soft">Lieu :</span> {obs.location_description}</div>
                  )}
                  {(obs.location_latitude || obs.location_longitude) && (
                    <div className="col-md-4">
                      <span className="text-soft">Coordonnées :</span>{" "}
                      {obs.location_latitude && obs.location_longitude
                        ? `${parseFloat(obs.location_latitude).toFixed(5)}, ${parseFloat(obs.location_longitude).toFixed(5)}`
                        : obs.location_latitude
                          ? `Lat. ${parseFloat(obs.location_latitude).toFixed(5)}`
                          : `Lng. ${parseFloat(obs.location_longitude).toFixed(5)}`}
                    </div>
                  )}
                  {obs.weather_conditions && (
                    <div className="col-md-4"><span className="text-soft">Météo :</span> {LABELS.weather_conditions[obs.weather_conditions] ?? obs.weather_conditions}</div>
                  )}
                </div>
              </div>
            )}

            {obs.improvement_proposal && (
              <div className="mb-4">
                <h6 className="text-soft mb-2">
                  <Icon name="bulb" className="me-2" />
                  Proposition d'amélioration
                </h6>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{obs.improvement_proposal}</p>
              </div>
            )}

            {(obs.correction_due_date || obs.estimated_cost != null) && (
              <div className="mb-4">
                <h6 className="text-soft mb-2">
                  <Icon name="calendar-check" className="me-2" />
                  Correction
                </h6>
                <div className="row g-2">
                  {obs.correction_due_date && (
                    <div className="col-md-4">
                      <span className="text-soft">Date prévue :</span>{" "}
                      {new Date(obs.correction_due_date).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  )}
                  {obs.estimated_cost != null && (
                    <div className="col-md-4">
                      <span className="text-soft">Coût estimé :</span>{" "}
                      {parseFloat(obs.estimated_cost).toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      FCFA
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 pt-3 border-top small text-soft">
              {obs.creator && (
                <span className="me-3">
                  <Icon name="user-circle" className="me-1" />
                  Créée par {`${obs.creator.first_name || ""} ${obs.creator.last_name || ""}`.trim() || obs.creator.email}
                </span>
              )}
              {obs.created_at && (
                <span>
                  <Icon name="calendar" className="me-1" />
                  {new Date(obs.created_at).toLocaleString("fr-FR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
              </>
            ) : (
              <div>
                {!Array.isArray(obs.attachments) || obs.attachments.length === 0 ? (
                  <div className="text-center py-4">
                    <Icon name="inbox" className="text-soft" style={{ fontSize: "2rem" }} />
                    <p className="text-soft mb-0 mt-2">Aucun fichier ou vidéo de preuve n&apos;est disponible.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Type</th>
                          <th>Taille</th>
                          <th>Ajouté le</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {obs.attachments.map((att) => {
                          const type = att.file_type || "";
                          const isImage = type.startsWith("image/");
                          const isVideo = type.startsWith("video/");
                          return (
                            <tr key={att.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Icon
                                    name={isImage ? "img" : isVideo ? "movie" : "file-doc"}
                                    className="me-2 text-primary"
                                  />
                                  <span className="fw-medium">{att.file_name || "Fichier"}</span>
                                </div>
                              </td>
                              <td className="text-soft small">{type || "—"}</td>
                              <td>{formatBytes(att.file_size)}</td>
                              <td className="text-soft small">
                                {att.created_at
                                  ? new Date(att.created_at).toLocaleString("fr-FR", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "—"}
                              </td>
                              <td className="text-end">
                                <Button
                                  type="button"
                                  onClick={() => openAttachment(att)}
                                  color="light"
                                  size="sm"
                                  className="me-1"
                                >
                                  Ouvrir
                                </Button>
                                <Button type="button" onClick={() => downloadAttachment(att)} color="primary" size="sm">
                                  Télécharger
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="light" onClick={onClose}>
              Fermer
            </Button>
            <PermissionGate permission={["observations.edit", "observations.update", "Observations - Modifier"]}>
              <Button color="primary" onClick={handleEdit}>
                <Icon name="edit" className="me-1" />
                Modifier
              </Button>
            </PermissionGate>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default ObservationViewModal;
